'use client';

import {
  useReducer,
  createContext,
  useContext,
  useMemo,
  Dispatch,
  ReactNode,
} from 'react';
import {
  useScoutContext,
  toScoutSelection,
  SCOUT_CONTEXT_KEYS,
} from 'src/hooks/scout';

// Types
type SortOption = {
  label: string;
  sort: string;
  order: 'asc' | 'desc';
  sort_search?: string;
  sort_value?: string;
};

type ResultsListState = {
  selectedProviders: MedScout.Opportunity[];
  selectedIds: string[];
  search: string;
  sort: SortOption | null;
  page: number;
  showAddToListsDialog: boolean;
};

type ResultsListContextType = {
  state: ResultsListState;
  dispatch: Dispatch<ResultsListAction>;
};

// Action Types
type ResultsListAction =
  | { type: 'SET_SELECTED_IDS'; payload: string[] }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_SORT'; payload: SortOption }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SELECT_ALL' }
  | { type: 'SET_SELECTED_PROVIDERS'; payload: MedScout.Opportunity[] }
  | { type: 'SET_SHOW_ADD_TO_LISTS_DIALOG'; payload: boolean };

// Initial State
const initialState: ResultsListState = {
  selectedIds: [],
  search: '',
  sort: null,
  page: 1,
  selectedProviders: [],
  showAddToListsDialog: false,
};

// Context
const ResultsListContext = createContext<ResultsListContextType | undefined>(
  undefined
);


// Reducer
const resultsListReducer = (
  state: ResultsListState,
  action: ResultsListAction
): ResultsListState => {
  switch (action.type) {
    case 'SET_SELECTED_IDS':
      return { ...state, selectedIds: action.payload };
    case 'SET_SEARCH':
      return { ...state, search: action.payload, page: 1 };
    case 'SET_SORT':
      return { ...state, sort: action.payload, page: 1 };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SELECT_ALL':
      return { ...state, selectedIds: [] };
    case 'SET_SELECTED_PROVIDERS':
      return { ...state, selectedProviders: action.payload };
    case 'SET_SHOW_ADD_TO_LISTS_DIALOG':
      return { ...state, showAddToListsDialog: action.payload };
    default:
      return state;
  }
};


// Provider Component
export const ResultsListProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(resultsListReducer, initialState);

  // Share the selected results (IDs and count only) with Scout.
  const scoutSelection = useMemo(
    () =>
      toScoutSelection(
        (state.selectedProviders ?? []).map((provider) => provider?.provider_id)
      ),
    [state.selectedProviders]
  );
  useScoutContext(
    SCOUT_CONTEXT_KEYS.DISCOVERY_SELECTION,
    'Selected Discovery results',
    scoutSelection
  );

  const value = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state]
  );

  return (
    <ResultsListContext.Provider value={value}>
      {children}
    </ResultsListContext.Provider>
  );
};

// Hook
export const useResultsList = (): ResultsListContextType => {
  const context = useContext(ResultsListContext);
  if (!context) {
    throw new Error('useResultsList must be used within a ResultsListProvider');
  }
  return context;
};
